import React, {useMemo, useState} from "react";
import s from "../../../staff/styles/staff.module.css";
import {Alert, Button, Card, Col, Divider, Form, Input, message, Row, Select, Switch, Tag} from "antd";


const AddObjFinanceIncome = (props) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState(1);

    // Фильтрация доступных объектов
    const availableObjects = useMemo(() => {
        if (props.page === 3) {
            return props.objects; // Редактирование - показываем все объекты
        }

        // Добавление - показываем только объекты без доходов
        return props.objects.filter(obj =>
            !props.income.some(income => Number(income.obj_id) === Number(obj.id))
        );
    }, [props.objects, props.income, props.page]);

    // Начальное состояние формы
    const initialFormData = useMemo(() => {
        if (props.page === 3 && props.current) {
            // Режим редактирования
            return {
                obj_id: Number(props.current.obj_id),
                obj_name: props.current.obj_name,
                enable: Number(props.current.enable) === 1,
                type: Number(props.current.type),
                nds: Number(props.current.nds),
                balance: Number(props.current.balance) || 0,
                // Разбиваем строку стоимости на массив месяцев
                monthlyCosts: props.current.cost.split('`').map(cost =>
                    parseFloat(cost.replace(',', '.')) || 0
                )
            };
        } else {
            // Режим добавления
            const firstObj = availableObjects[0];
            return {
                obj_id: firstObj ? Number(firstObj.id) : 0,
                obj_name: firstObj ? firstObj.name : '',
                enable: true,
                type: 1,
                nds: 0,
                balance: 0,
                monthlyCosts: Array(12).fill(0) // 12 месяцев по 0
            };
        }
    }, [props.page, props.current, availableObjects]);

    const [formData, setFormData] = useState(initialFormData);

    // Обработчик изменения объекта
    const handleObjectChange = (objId) => {
        const selectedObj = availableObjects.find(obj => Number(obj.id) === Number(objId));
        if (selectedObj) {
            setFormData(prev => ({
                ...prev,
                obj_id: Number(objId),
                obj_name: selectedObj.name
            }));
        }
    };

    // Обработчик изменения типа оплаты
    const handleTypeChange = (type) => {
        setSelectedType(type);
        setFormData(prev => ({...prev, type: Number(type)}));
    };

    // Обработчик изменения стоимости за месяц
    const handleMonthCostChange = (monthIndex, value) => {
        const numericValue = parseFloat(value) || 0;
        setFormData(prev => {
            const newMonthlyCosts = [...prev.monthlyCosts];
            newMonthlyCosts[monthIndex] = numericValue;
            return {...prev, monthlyCosts: newMonthlyCosts};
        });
    };

    // Валидация формы
    const validateForm = () => {
        if (formData.obj_id === 0) {
            message.error('Выберите объект');
            return false;
        }

        if (formData.monthlyCosts.every(cost => cost === 0)) {
            message.warning('Укажите стоимость хотя бы для одного месяца');
            return false;
        }

        return true;
    };

    // Отправка данных на сервер
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);

        try {
            // Формируем данные для отправки
            const submitData = {
                id: props.page === 3 ? Number(props.current.id) : 0,
                obj_id: formData.obj_id, // Добавляем obj_id
                obj_name: formData.obj_name,
                enable: formData.enable ? 1 : 0,
                cost: formData.monthlyCosts, // Массив значений
                type: formData.type,
                nds: formData.nds,
                balance: formData.balance,
                update: props.page === 3 ? 1 : 0, // Добавляем флаг обновления
                period: 3
            };

            console.log('Отправляемые данные:', submitData);

            // Вызываем функцию отправки из props
            await props.postIncome(submitData, props.page === 3 ? 1 : 0);

            message.success(
                props.page === 3
                    ? 'Данные объекта успешно обновлены'
                    : 'Объект успешно добавлен'
            );

            props.close();
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            message.error('Произошла ошибка при сохранении данных');
        } finally {
            setLoading(false);
        }
    };

    // Названия месяцев
    const monthNames = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];

    // Сумма за все месяцы
    const totalYearlyCost = formData.monthlyCosts.reduce((sum, cost) => sum + cost, 0);

    return (
        <div className={s.addStaffCont}>
            <Card
                title={
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span>
                            {props.page === 3 ? 'Редактирование объекта' : 'Добавление нового объекта'}
                        </span>
                        <Button type="link" onClick={props.close}>
                            Закрыть
                        </Button>
                    </div>
                }
                className="income-object-card"
            >
                <Form
                    form={form}
                    labelCol={{span: 6}}
                    wrapperCol={{span: 18}}
                    layout="horizontal"
                    size="middle"
                >
                    <Divider orientation="left">Основные данные</Divider>

                    <Form.Item label="Объект" required>
                        <Select
                            value={formData.obj_id}
                            onChange={handleObjectChange}
                            disabled={props.page === 3} // При редактировании нельзя менять объект
                        >
                            {availableObjects.map(obj => (
                                <Select.Option key={obj.id} value={Number(obj.id)}>
                                    {obj.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Тип оплаты">
                        <Select value={formData.type} onChange={handleTypeChange}>
                            <Select.Option value={1}>Наличные</Select.Option>
                            <Select.Option value={2}>Безналичные</Select.Option>
                        </Select>
                    </Form.Item>

                    {formData.type === 2 && (
                        <Form.Item label="Ставка НДС">
                            <Select
                                value={formData.nds}
                                onChange={(value) => setFormData(prev => ({...prev, nds: Number(value)}))}
                            >
                                <Select.Option value={0}>Без НДС</Select.Option>
                                <Select.Option value={7}>7%</Select.Option>
                                <Select.Option value={21}>21%</Select.Option>
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item label="Начальный баланс">
                        <Input
                            type="number"
                            step="0.01"
                            value={formData.balance}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                balance: parseFloat(e.target.value) || 0
                            }))}
                            prefix="€"
                        />
                    </Form.Item>

                    <Form.Item label="Статус объекта">
                        <Switch
                            checked={formData.enable}
                            onChange={(checked) => setFormData(prev => ({...prev, enable: checked}))}
                            checkedChildren="Активен"
                            unCheckedChildren="Неактивен"
                        />
                    </Form.Item>

                    <Divider orientation="left">
                        Абонентская плата по месяцам
                        <Tag color="blue" style={{marginLeft: 8}}>
                            Итого за год: {totalYearlyCost.toFixed(2)}€
                        </Tag>
                    </Divider>

                    <Row gutter={[16, 8]}>
                        {monthNames.map((monthName, index) => (
                            <Col span={8} key={index}>
                                <Form.Item label={monthName}>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={formData.monthlyCosts[index]}
                                        onChange={(e) => handleMonthCostChange(index, e.target.value)}
                                        prefix="€"
                                        placeholder="0.00"
                                    />
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>

                    <Form.Item wrapperCol={{offset: 6, span: 18}}>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={loading}
                            style={{marginRight: 8}}
                        >
                            {props.page === 3 ? 'Обновить данные' : 'Добавить объект'}
                        </Button>
                        <Button onClick={props.close}>
                            Отмена
                        </Button>
                    </Form.Item>
                </Form>

                {/* Отладочная информация */}
                {process.env.NODE_ENV === 'development' && (
                    <Alert
                        message="Отладочная информация"
                        description={
                            <div>
                                <p>Массив cost: {JSON.stringify(formData.monthlyCosts)}</p>
                                <p>Тип операции: {props.page === 3 ? 'UPDATE' : 'INSERT'}</p>
                                <p>ID объекта: {formData.obj_id}</p>
                            </div>
                        }
                        type="info"
                        showIcon
                    />
                )}
            </Card>
        </div>
    );
};

export default AddObjFinanceIncome;